import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import apiClient from '../api/client';
import { 
  ArrowLeft, FileText, Car, Trash2, Edit, AlertTriangle, 
  Download, CheckCircle, XCircle, Clock, MapPin, Calendar,
  Gauge, TrendingUp, Receipt
} from 'lucide-react';
import { formatCurrencyAmount } from '../components/CurrencyDisplay';

export default function ExpenseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [expense, setExpense] = useState(null);
  const [trips, setTrips] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [mileageTracker, setMileageTracker] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchExpenseDetails();
  }, [id]);

  const fetchExpenseDetails = async () => {
    try {
      setLoading(true);
      
      // Fetch expense details
      const expenseResponse = await apiClient.get(`/expenses/${id}`);
      const responseData = expenseResponse.data;
      
      // Extract the nested expense object from the response
      setExpense(responseData.expense);
      
      // Use documents from the main response
      setDocuments(responseData.documents || []);

      // Fetch mileage trips if it's a mileage expense
      if (responseData.expense?.Category === 'Mileage' || responseData.expense?.Purpose?.includes('Mileage')) {
        try {
          const tripsResponse = await apiClient.get(`/expenses/${id}/mileage-trips`);
          setTrips(tripsResponse.data.trips || []);
        } catch (error) {
          console.error('Error fetching trips:', error);
          setTrips([]);
        }
      }

      // Fetch mileage tracker status
      if (responseData.expense?.Category === 'Mileage' || responseData.expense?.Purpose?.includes('Mileage')) {
        try {
          const trackerResponse = await apiClient.get('/expenses/user-mileage-tracker/status');
          setMileageTracker(trackerResponse.data);
        } catch (error) {
          console.error('Error fetching mileage tracker:', error);
        }
      }

    } catch (error) {
      console.error('Failed to fetch expense details:', error);
      alert('Failed to load expense details');
      navigate('/expenses');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this expense? This action cannot be undone.')) {
      return;
    }

    try {
      setDeleting(true);
      await apiClient.delete(`/expenses/${id}`);
      alert('Expense deleted successfully');
      navigate('/expenses');
    } catch (error) {
      console.error('Failed to delete expense:', error);
      alert(error.response?.data?.message || 'Failed to delete expense');
    } finally {
      setDeleting(false);
    }
  };

  const handleEdit = () => {
    navigate(`/expenses/${id}/edit`);
  };

  const getStatusBadge = (status) => {
    const styles = {
      'Draft': 'bg-gray-100 text-gray-800',
      'Pending Approval': 'bg-yellow-100 text-yellow-800',
      'Approved': 'bg-green-100 text-green-800',
      'Rejected': 'bg-red-100 text-red-800',
      'Posted': 'bg-blue-100 text-blue-800'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading expense details...</p>
        </div>
      </div>
    );
  }

  if (!expense) {
    return (
      <div className="p-6 max-w-[1400px] mx-auto">
        <div className="text-center">
          <AlertTriangle className="mx-auto h-12 w-12 text-yellow-400" />
          <h3 className="mt-2 text-lg font-medium text-gray-900">Expense not found</h3>
          <div className="mt-6">
            <Link to="/expenses" className="text-blue-600 hover:text-blue-800">
              ← Back to Expenses
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const isMileageExpense = expense.Category === 'Mileage' || expense.Purpose?.includes('Mileage');
  const canEdit = ['Draft', 'Rejected', 'Pending'].includes(expense.Status);
  const canDelete = expense.Status === 'Draft' || expense.Status === 'Rejected';

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="mb-6">
        <Link
          to="/expenses"
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Expenses
        </Link>
        
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Receipt className="w-7 h-7 text-blue-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{expense.ExpenseNumber}</h1>
              <p className="text-sm text-gray-500">
                Submitted on {new Date(expense.SubmittedDate || expense.CreatedAt).toLocaleDateString()}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {getStatusBadge(expense.Status)}
            {canEdit && (
              <button
                onClick={handleEdit}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </button>
            )}
            {canDelete && (
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="inline-flex items-center px-4 py-2 border border-red-300 rounded-md shadow-sm text-sm font-medium text-red-700 bg-white hover:bg-red-50 disabled:opacity-50"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Expense Details Card */}
          <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Expense Details</h2>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <dt className="text-sm font-medium text-gray-500">Date</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {new Date(expense.ExpenseDate).toLocaleDateString()}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Department</dt>
                <dd className="mt-1 text-sm text-gray-900">{expense.DepartmentName || 'N/A'}</dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="text-sm font-medium text-gray-500">Purpose</dt>
                <dd className="mt-1 text-sm text-gray-900">{expense.Purpose}</dd>
              </div>
              {expense.Category && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Category</dt>
                  <dd className="mt-1 text-sm text-gray-900">{expense.Category}</dd>
                </div>
              )}
              {expense.Notes && (
                <div className="sm:col-span-2">
                  <dt className="text-sm font-medium text-gray-500">Notes</dt>
                  <dd className="mt-1 text-sm text-gray-900">{expense.Notes}</dd>
                </div>
              )}
            </dl>
          </div>

          {/* Mileage Trips Table */}
          {isMileageExpense && trips.length > 0 && (
            <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Car className="h-5 w-5 mr-2" />
                Mileage Trips ({trips.length})
              </h2>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">From</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">To</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Distance</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rate</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {trips.map((trip, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {new Date(trip.ExpenseDate).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {trip.MileageFromLocation}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {trip.MileageToLocation}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {trip.MileageDistance} miles
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          £{trip.MileageRatePerMile}/mile
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          {formatCurrencyAmount(trip.GrossAmount, expense.Currency || 'GBP')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50">
                    <tr>
                      <td colSpan="3" className="px-4 py-3 text-sm font-medium text-gray-900 text-right">
                        Total:
                      </td>
                      <td className="px-4 py-3 text-sm font-semibold text-gray-900">
                        {trips.reduce((sum, trip) => sum + parseFloat(trip.MileageDistance || 0), 0).toFixed(2)} miles
                      </td>
                      <td></td>
                      <td className="px-4 py-3 text-sm font-semibold text-gray-900">
                        {formatCurrencyAmount(expense.TotalAmount, expense.Currency || 'GBP')}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}

          {/* Documents */}
          {documents.length > 0 && (
            <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <FileText className="h-5 w-5 mr-2" />
                Documents ({documents.length})
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {documents.map((doc) => (
                  <div key={doc.ExpenseDocumentID} className="border border-gray-200 rounded-lg p-4">
                    {/* Document Preview */}
                    {doc.BlobURL && (doc.MimeType?.startsWith('image/') || doc.FileName?.match(/\.(jpg|jpeg|png|gif)$/i)) ? (
                      <div className="mb-3">
                        <img
                          src={doc.BlobURL}
                          alt={doc.FileName}
                          className="w-full h-48 object-contain bg-gray-50 rounded"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        <div className="hidden w-full h-48 bg-gray-50 rounded items-center justify-center">
                          <FileText className="h-12 w-12 text-gray-400" />
                        </div>
                      </div>
                    ) : (
                      <div className="mb-3 w-full h-48 bg-gray-50 rounded flex items-center justify-center">
                        <FileText className="h-12 w-12 text-gray-400" />
                      </div>
                    )}
                    
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-gray-900 truncate" title={doc.FileName}>
                        {doc.FileName}
                      </p>
                      <p className="text-xs text-gray-500">
                        Uploaded {new Date(doc.UploadedAt).toLocaleDateString()}
                      </p>
                      {doc.DocumentType && (
                        <span className="inline-block px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
                          {doc.DocumentType}
                        </span>
                      )}
                      {doc.BlobURL && (
                        <a
                          href={doc.BlobURL}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800 mt-2"
                        >
                          <Download className="h-4 w-4 mr-1" />
                          View Full Size
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right Column - Summary */}
        <div className="space-y-6">
          {/* Financial Summary */}
          <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Financial Summary</h2>
            <dl className="space-y-3">
              <div className="flex justify-between">
                <dt className="text-sm text-gray-500">Subtotal</dt>
                <dd className="text-sm font-medium text-gray-900">
                  {formatCurrencyAmount(expense.SubTotal, expense.Currency || 'GBP')}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm text-gray-500">VAT</dt>
                <dd className="text-sm font-medium text-gray-900">
                  {formatCurrencyAmount(expense.VATAmount, expense.Currency || 'GBP')}
                </dd>
              </div>
              <div className="flex justify-between pt-3 border-t border-gray-200">
                <dt className="text-base font-semibold text-gray-900">Total</dt>
                <dd className="text-base font-semibold text-gray-900">
                  {formatCurrencyAmount(expense.TotalAmount, expense.Currency || 'GBP')}
                </dd>
              </div>
            </dl>
          </div>

          {/* Mileage Tracker Status */}
          {isMileageExpense && mileageTracker && (
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 shadow rounded-lg p-6 border border-blue-200">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Gauge className="h-5 w-5 mr-2 text-blue-600" />
                Tax Year Mileage Tracker
              </h2>
              
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Current Tax Year</p>
                  <p className="text-lg font-semibold text-gray-900">{mileageTracker.TaxYear}</p>
                  <p className="text-xs text-gray-500">
                    {new Date(mileageTracker.TaxYearStartDate).toLocaleDateString()} - {new Date(mileageTracker.TaxYearEndDate).toLocaleDateString()}
                  </p>
                </div>

                <div className="border-t border-blue-200 pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-600">Total Miles Claimed</span>
                    <span className="text-xl font-bold text-gray-900">
                      {mileageTracker.TotalMilesSubmitted?.toLocaleString()} mi
                    </span>
                  </div>
                  
                  {/* Progress Bar */}
                  <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                    <div
                      className={`h-3 rounded-full transition-all ${
                        mileageTracker.TotalMilesSubmitted >= 10000 
                          ? 'bg-orange-500' 
                          : 'bg-blue-600'
                      }`}
                      style={{ width: `${Math.min((mileageTracker.TotalMilesSubmitted / 10000) * 100, 100)}%` }}
                    ></div>
                  </div>
                  
                  <p className="text-xs text-gray-500 text-center">
                    {mileageTracker.TotalMilesSubmitted >= 10000 
                      ? 'Threshold reached - reduced rate applies'
                      : `${(10000 - mileageTracker.TotalMilesSubmitted).toLocaleString()} miles until reduced rate`
                    }
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4 border-t border-blue-200 pt-4">
                  <div>
                    <p className="text-xs text-gray-600 mb-1">Standard Rate Miles</p>
                    <p className="text-lg font-semibold text-green-700">
                      {mileageTracker.TotalStandardRateMiles?.toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-500">@ £{mileageTracker?.CurrentApplicableRate?.toFixed(2) || '0.55'}/mi</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 mb-1">Reduced Rate Miles</p>
                    <p className="text-lg font-semibold text-orange-700">
                      {mileageTracker.TotalReducedRateMiles?.toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-500">@ £0.25/mi</p>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-3 border border-blue-200">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Current Rate</span>
                    <span className="text-lg font-bold text-blue-600">
                      £{mileageTracker.CurrentApplicableRate}/mile
                    </span>
                  </div>
                  {mileageTracker.ThresholdReachedDate && (
                    <p className="text-xs text-gray-500 mt-1">
                      Threshold reached on {new Date(mileageTracker.ThresholdReachedDate).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Approval Status */}
          {expense.Status !== 'Draft' && (
            <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Approval Status</h2>
              <div className="space-y-3">
                {expense.Status === 'Pending Approval' && (
                  <div className="flex items-start">
                    <Clock className="h-5 w-5 text-yellow-500 mr-3 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Pending Review</p>
                      <p className="text-xs text-gray-500 mt-1">
                        Awaiting approval from department manager
                      </p>
                    </div>
                  </div>
                )}
                {expense.Status === 'Approved' && (
                  <div className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Approved</p>
                      {expense.ApprovedBy && (
                        <p className="text-xs text-gray-500 mt-1">
                          By {expense.ApprovedByName} on {new Date(expense.ApprovedDate).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>
                )}
                {expense.Status === 'Rejected' && (
                  <div className="flex items-start">
                    <XCircle className="h-5 w-5 text-red-500 mr-3 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Rejected</p>
                      {expense.RejectionReason && (
                        <p className="text-xs text-gray-600 mt-1 bg-red-50 p-2 rounded">
                          {expense.RejectionReason}
                        </p>
                      )}
                    </div>
                  </div>
                )}
                {expense.Status === 'Posted' && (
                  <div className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-blue-500 mr-3 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Posted</p>
                      <p className="text-xs text-gray-500 mt-1">
                        Payment processed
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
